import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  Alert,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { ConditionalLogicRule, ConditionalLogicCondition, FieldOption } from '../../types/questionnaire.types';
import { REFERRAL_CONDITION_KEY, LEAD_CONDITION_KEY, LEAD_PRESENT_VALUE } from '../../utils/conditionalLogic';
import { useReferralOptions } from '../../hooks/useReferralOptions';

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In (comma-separated values)' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'greater_or_equal', label: 'Greater or Equal' },
  { value: 'less_or_equal', label: 'Less or Equal' },
];

interface AvailableField {
  name: string;
  label: string;
  type: string;
  options?: FieldOption[];
}

interface FieldConditionalLogicEditorProps {
  rule?: ConditionalLogicRule;
  onChange: (rule?: ConditionalLogicRule) => void;
  availableFields: AvailableField[];
  currentFieldName: string;
}

// Build initial conditions list from a saved rule.
// Prefers `conditions` (new multi-condition form), falls back to single `showIf` (legacy).
const initialConditions = (rule?: ConditionalLogicRule): ConditionalLogicCondition[] => {
  if (!rule) return [];
  if (rule.conditions && rule.conditions.length > 0) {
    return rule.conditions.map((c) => ({
      question: c.question || '',
      value: c.value ?? '',
      operator: c.operator || 'equals',
      ...(c.referral_id !== undefined ? { referral_id: c.referral_id } : {}),
    }));
  }
  return [
    {
      question: rule.showIf?.question || '',
      value: rule.showIf?.value ?? '',
      operator: rule.showIf?.operator || 'equals',
      ...(rule.showIf?.referral_id !== undefined ? { referral_id: rule.showIf.referral_id } : {}),
    },
  ];
};

// Referral conditions store one id or a list of ids in `referral_id`.
const toReferralIds = (raw: any): number[] => {
  if (Array.isArray(raw)) return raw.map(Number).filter((n) => !isNaN(n));
  if (typeof raw === 'number') return [raw];
  return [];
};

// Coerce stored value into a string[] for the multi-select case.
// Supports legacy rules where value was stored as a comma-separated string.
const toMultiValue = (raw: any): string[] => {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  }
  return [];
};

export const FieldConditionalLogicEditor: React.FC<FieldConditionalLogicEditorProps> = ({
  rule,
  onChange,
  availableFields,
  currentFieldName,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [conditions, setConditions] = useState<ConditionalLogicCondition[]>(initialConditions(rule));
  const referralOptions = useReferralOptions();

  // Re-seed from the saved rule only when the editor switches to a different
  // field. Not on every `rule` change: we now propagate edits live, so a
  // rule-triggered reset would drop a row the user is still filling in.
  useEffect(() => {
    setConditions(initialConditions(rule));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFieldName]);

  const hasRule = conditions.length > 0;

  const handleAddRule = () => {
    setConditions([{ question: '', value: '', operator: 'equals' }]);
    setExpanded(true);
  };

  // Build the rule from the current conditions and push it to the parent, so it
  // is saved together with the field - there is no separate "Apply" step.
  // Conditions still being set up (no question chosen yet) are left out.
  const buildRule = (conds: ConditionalLogicCondition[]): ConditionalLogicRule | undefined => {
    const valid = conds.filter((c) => c.question && String(c.question).trim() !== '');
    if (valid.length === 0) return undefined;
    const first = valid[0];
    // `showIf` mirrors the first condition for backward compatibility with any
    // reader that hasn't been updated to use `conditions`.
    return {
      field: currentFieldName,
      showIf: {
        question: first.question,
        value: first.value,
        operator: first.operator || 'equals',
        ...(first.referral_id !== undefined ? { referral_id: first.referral_id } : {}),
      },
      ...(valid.length > 1 ? { conditions: valid } : {}),
    };
  };

  // Keep the in-progress rows visible locally, and propagate the rule live.
  const commit = (next: ConditionalLogicCondition[]) => {
    setConditions(next);
    onChange(buildRule(next));
  };

  const handleAddCondition = () => {
    commit([...conditions, { question: '', value: '', operator: 'equals' }]);
  };

  const handleUpdateCondition = (index: number, updates: Partial<ConditionalLogicCondition>) => {
    commit(conditions.map((c, i) => (i === index ? { ...c, ...updates } : c)));
  };

  const handleRemoveCondition = (index: number) => {
    commit(conditions.filter((_, i) => i !== index));
  };

  const handleClear = () => {
    setConditions([]);
    onChange(undefined);
    setExpanded(false);
  };

  const renderConditionRow = (cond: ConditionalLogicCondition, index: number) => {
    const selectedField = availableFields.find((f) => f.name === cond.question);
    const isChoiceField =
      selectedField?.type &&
      ['radio', 'checkbox', 'checkboxes', 'select', 'multiselect', 'button-group'].includes(selectedField.type);
    const operator = cond.operator || 'equals';
    const isInOperator = operator === 'in';
    // The referral isn't an answer — it comes from the ?referral=<hash> link and
    // its value is the referral name, so it takes a free-text value (comma-separated for "in").
    const isReferralCondition = cond.question === REFERRAL_CONDITION_KEY;
    // The lead condition depends on whether the questionnaire is tied to a lead,
    // not on an answer. It uses a single combined "show when" control instead of
    // the operator + value pair.
    const isLeadCondition = cond.question === LEAD_CONDITION_KEY;
    const shouldShowValueInput =
      !isLeadCondition &&
      !['is_empty', 'is_not_empty'].includes(operator) &&
      (isReferralCondition || !(isInOperator && !isChoiceField));

    return (
      <Box
        key={index}
        sx={{
          mb: 1.5,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'grey.50',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          {/* Dependency Field */}
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>When this question</InputLabel>
              <Select
                value={cond.question || ''}
                label="When this question"
                onChange={(e) => {
                  const q = e.target.value;
                  if (q === LEAD_CONDITION_KEY) {
                    // Seed the combined control: show when a lead exists.
                    handleUpdateCondition(index, {
                      question: q, operator: 'equals', value: LEAD_PRESENT_VALUE, referral_id: undefined,
                    });
                  } else {
                    handleUpdateCondition(index, { question: q, value: '' });
                  }
                }}
              >
                <MenuItem value="">Select a field...</MenuItem>
                <MenuItem value={REFERRAL_CONDITION_KEY}>Referral (from link)</MenuItem>
                <MenuItem value={LEAD_CONDITION_KEY}>Lead (from questionnaire)</MenuItem>
                {availableFields
                  .filter((f) => f.name !== currentFieldName)
                  .map((field) => (
                    <MenuItem key={field.name} value={field.name}>
                      {field.label} ({field.name})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Operator (hidden for the lead condition, which uses a combined control) */}
          {!isLeadCondition && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Operator</InputLabel>
                <Select
                  value={operator}
                  label="Operator"
                  onChange={(e) => {
                    const nextOperator = e.target.value;
                    // Reset value when switching between scalar and multi-value operators
                    // so we don't leave incompatible data (string vs array) in state.
                    const nextValue = nextOperator === 'in' ? [] : '';
                    handleUpdateCondition(index, { operator: nextOperator, value: nextValue });
                  }}
                >
                  {OPERATORS.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Lead existence: a single "show when" control (present / absent) */}
          {isLeadCondition && (
            <Grid item xs={12} sm={6.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Show this question when</InputLabel>
                <Select
                  value={operator === 'not_equals' ? 'absent' : 'present'}
                  label="Show this question when"
                  onChange={(e) =>
                    handleUpdateCondition(index, {
                      operator: e.target.value === 'absent' ? 'not_equals' : 'equals',
                      value: LEAD_PRESENT_VALUE,
                    })
                  }
                >
                  <MenuItem value="present">A lead exists</MenuItem>
                  <MenuItem value="absent">No lead exists</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Value Input */}
          {shouldShowValueInput && (
            <Grid item xs={12} sm={3.5}>
              {isReferralCondition ? (
                isInOperator ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Referrals</InputLabel>
                    <Select
                      multiple
                      value={toReferralIds(cond.referral_id).map(String)}
                      label="Referrals"
                      onChange={(e) => {
                        const next = e.target.value;
                        const ids = (Array.isArray(next) ? next : [next]).map(Number);
                        handleUpdateCondition(index, { referral_id: ids, value: '' });
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((v) => (
                            <Chip
                              key={v}
                              size="small"
                              label={referralOptions.find((o) => String(o.id) === v)?.name ?? `#${v}`}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {referralOptions.map((option) => {
                        const selectedIds = toReferralIds(cond.referral_id);
                        return (
                          <MenuItem key={option.id} value={String(option.id)}>
                            <Checkbox checked={selectedIds.includes(option.id)} />
                            <ListItemText primary={option.name} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Referral</InputLabel>
                    <Select
                      value={
                        typeof cond.referral_id === 'number' ? String(cond.referral_id) : ''
                      }
                      label="Referral"
                      onChange={(e) =>
                        handleUpdateCondition(index, {
                          referral_id: e.target.value === '' ? undefined : Number(e.target.value),
                          value: '',
                        })
                      }
                    >
                      <MenuItem value="">Select a referral...</MenuItem>
                      {referralOptions.map((option) => (
                        <MenuItem key={option.id} value={String(option.id)}>
                          {option.name}
                        </MenuItem>
                      ))}
                      {/* Keep a configured referral that is no longer in the list selectable */}
                      {typeof cond.referral_id === 'number' &&
                        !referralOptions.some((o) => o.id === cond.referral_id) && (
                          <MenuItem value={String(cond.referral_id)}>
                            Referral #{cond.referral_id} (not found)
                          </MenuItem>
                        )}
                    </Select>
                  </FormControl>
                )
              ) : isChoiceField && selectedField?.options && selectedField.options.length > 0 ? (
                isInOperator ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Values</InputLabel>
                    <Select
                      multiple
                      value={toMultiValue(cond.value)}
                      label="Values"
                      onChange={(e) => {
                        const next = e.target.value;
                        handleUpdateCondition(index, {
                          value: Array.isArray(next) ? next : [next],
                        });
                      }}
                      renderValue={(selected) => {
                        const arr = selected as string[];
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {arr.map((v) => {
                              const opt = selectedField.options?.find((o) => o.value === v);
                              return <Chip key={v} size="small" label={opt?.label ?? v} />;
                            })}
                          </Box>
                        );
                      }}
                    >
                      {selectedField.options.map((opt) => {
                        const selectedArr = toMultiValue(cond.value);
                        return (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Checkbox checked={selectedArr.includes(opt.value)} />
                            <ListItemText primary={opt.label} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Value</InputLabel>
                    <Select
                      value={cond.value || ''}
                      label="Value"
                      onChange={(e) => handleUpdateCondition(index, { value: e.target.value })}
                    >
                      <MenuItem value="">Select value...</MenuItem>
                      {selectedField.options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Value"
                  value={Array.isArray(cond.value) ? cond.value.join(', ') : cond.value || ''}
                  onChange={(e) => {
                    let value: any = e.target.value;
                    if (selectedField?.type === 'number' && value && !isNaN(Number(value))) {
                      value = Number(value);
                    }
                    handleUpdateCondition(index, { value });
                  }}
                  placeholder="e.g., yes or 50"
                />
              )}
            </Grid>
          )}

          {/* Per-row delete */}
          <Grid item xs={12} sm={0.5} display="flex" justifyContent="flex-end">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemoveCondition(index)}
              aria-label="Remove condition"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          p: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: 'grey.100' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {hasRule ? '✓ Show/Hide Logic' : 'Show/Hide Logic (Optional)'}
        </Typography>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 2, pt: 1, pb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Define when this field should be visible based on answers to other fields.
            {conditions.length > 1 && ' All conditions must be true (AND).'}
          </Alert>

          {hasRule ? (
            <>
              {conditions.map((cond, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <Divider sx={{ my: 1 }}>
                      <Chip label="AND" size="small" color="primary" variant="outlined" />
                    </Divider>
                  )}
                  {renderConditionRow(cond, index)}
                </React.Fragment>
              ))}

              <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddCondition}
                  variant="outlined"
                >
                  Add Condition
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleClear}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddRule}
              variant="outlined"
            >
              Add Rule
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
